import BaseService from './BaseService'
import deviceService from './DeviceService'
import fileService from './FileService'
import firebaseService from './FirebaseService.js'
import { db } from '@/config/firebase'
import { collection, query, where, getDocs } from 'firebase/firestore'

class ScreenService extends BaseService {
  constructor() {
    super('digiscreens');
  }

  /**
   * Get all screens for a user with caching
   */
  async getUserScreens(userId, useCache = true) {
    try {
      const screens = await this.getDocuments({ uid: userId }, null, null, useCache);
      return screens;
    } catch (error) {
      console.error('Error loading user screens:', error);
      throw error;
    }
  }

  /**
   * Get a specific screen by ID with caching
   */
  async getScreen(screenId, useCache = true) {
    try {
      const screen = await this.getDocument(screenId, useCache);
      return screen;
    } catch (error) {
      console.error('Error loading screen:', error);
      throw error;
    }
  }

  /**
   * Get default screen for a user
   */
  async getDefaultScreen(userId, useCache = true) {
    try {
      const screens = await this.getUserScreens(userId, useCache);
      return screens.find(screen => screen.isDefault === true);
    } catch (error) {
      console.error('Error loading default screen:', error);
      throw error;
    }
  }

  /**
   * Get a screen by name for a user
   */
  async getScreenByName(userId, screenName, useCache = true) {
    try {
      const screens = await this.getUserScreens(userId, useCache);
      return screens.find(screen => screen.name === screenName);
    } catch (error) {
      console.error('Error loading screen by name:', error);
      throw error;
    }
  }

  /**
   * Create a new screen
   * Based on DigiScreenManager logic (simplified)
   */
  async createScreen(screenData, imageVideoObject, userId) {
    try {
      // Add metadata
      screenData.uid = userId;
      screenData.created = new Date().getTime();
      screenData.version = 2;

      // Create the screen document
      const result = await this.createDocument(screenData);
      const screenKey = result.key;

      // Set as default if specified
      if (screenData.isDefault) {
        await this.setDefaultScreen(userId, screenKey, true);
      }

      // Handle file upload if provided
      if (imageVideoObject) {
        // Determine file type and name
        let fileName = screenKey + "_image.jpeg";
        if (imageVideoObject.indexOf("data:image/png;base64") !== -1) {
          fileName = screenKey + "_image.png";
        } else if (imageVideoObject.indexOf("data:image/jpg;base64") !== -1) {
          fileName = screenKey + "_image.jpg";
        } else if (imageVideoObject.indexOf("data:video/mp4;base64") !== -1) {
          fileName = screenKey + "_video.mp4";
        }

        // Upload file using FileService (based on DigiScreenManager pattern)
        const uploadResult = await fileService.uploadFile(fileName, imageVideoObject);
        
        const isVideo = fileName.indexOf(".mp4") !== -1;
        
        if (isVideo) {
          // For video files - uploadResult contains Cloudflare URL and thumbnail URLs
          await this.updateDocument(screenKey, {
            videoUrl: uploadResult.videoUrl,
            thumbUrl: uploadResult.thumbUrl,
            thumbUniversalUrl: uploadResult.thumbUniversalUrl
          });
        } else if (typeof uploadResult === 'object' && uploadResult.imageUrl && uploadResult.imageUniversalUrl) {
          // For image files with both URLs (new client-side resizing)
          await this.updateDocument(screenKey, {
            imageUrl: uploadResult.imageUrl,
            imageUniversalUrl: uploadResult.imageUniversalUrl
          });
        } else {
          // For image files - uploadResult is a simple URL string (legacy Firebase)
          await this.updateDocument(screenKey, {
            imageUrl: uploadResult,
            imageUniversalUrl: uploadResult.replace("_1920x1080.", "_1920x1920.")
          });
        }
      }

      // Invalidate cache to ensure fresh data
      this.clearCache();

      return { key: screenKey, ...screenData };
    } catch (error) {
      console.error('Error creating screen:', error);
      throw error;
    }
  }

  /**
   * Delete a screen and all associated files
   * Handles both Firebase and Cloudflare file deletion
   * 
   * Process:
   * 1. Get screen data (for video URL extraction)
   * 2. Remove screen references from devices
   * 3. Delete screen document from database
   * 4. Delete files from Firebase storage (thumbnails, images)
   * 5. Delete videos from Cloudflare (using screen data retrieved in step 1)
   * 
   * Based on DigiScreenManager logic
   */
  async deleteScreen(screenId, userId) {
    console.log('🔍 ScreenService.deleteScreen called:', { screenId, userId });
    
    try {
      // Get screen data BEFORE deleting the document
      console.log('📄 Getting screen data for video URL extraction...');
      const screen = await this.getScreen(screenId, false); // Force fresh data
      if (!screen) {
        console.log(`⚠️ Screen ${screenId} not found, cannot proceed with deletion`);
        throw new Error(`Screen ${screenId} not found`);
      }
      console.log(`✅ Retrieved screen data for ${screenId}`);

      // Get all devices to check for references
      console.log('📱 Getting user devices to check for screen references...');
      const devices = await deviceService.getUserDevices(userId, false); // Force fresh data
      console.log(`📱 Found ${devices.length} devices to check`);
      
      const promises = [this.deleteDocument(screenId)];
      console.log('🗑️ Added screen document deletion to promises');

      // Check each device for screen references
      let devicesToUpdate = 0;
      devices.forEach(device => {
        const screenData = {};
        let screenChanged = false;

        // Check if device is using this screen
        if (device.screenId && device.screenId === screenId) {
          console.log(`📱 Device ${device.key} (${device.name}) is using screen ${screenId} - will clear screenId`);
          screenData.screenId = "";
          screenChanged = true;
        }

        // Check if device has schedules using this screen
        if (device.schedule && device.schedule.length) {
          const schedulesUsingScreen = device.schedule.filter(sch => sch.screenId === screenId);
          if (schedulesUsingScreen.length > 0) {
            console.log(`📱 Device ${device.key} (${device.name}) has ${schedulesUsingScreen.length} schedules using screen ${screenId} - will remove them`);
            screenData.schedule = device.schedule.filter(sch => sch.screenId !== screenId);
            screenChanged = true;
          }
        }

        // Update device if screen references were found
        if (screenChanged) {
          promises.push(deviceService.updateDevice(device.key, screenData));
          devicesToUpdate++;
        }
      });

      console.log(`📱 Will update ${devicesToUpdate} devices that reference screen ${screenId}`);

      // Execute all updates (delete screen + update devices)
      console.log('⚡ Executing all deletion and update operations...');
      await Promise.all(promises);
      console.log('✅ All database operations completed successfully');

      // Delete associated files using FirebaseService
      console.log('🗂️ Starting file deletion process...');
      try {
        const fileDeletionResult = await firebaseService.deleteFiles(screenId);
        console.log(`✅ File deletion completed for screen ${screenId}:`, fileDeletionResult);
      } catch (error) {
        console.error(`❌ Failed to delete files for screen ${screenId}:`, error);
        // Continue with screen deletion even if file deletion fails
      }

      // Delete video from Cloudflare using the screen data we retrieved earlier
      console.log('🎥 Checking for video files to delete from Cloudflare...');
      try {
        await this.deleteVideoFromCloudflareUsingScreenData(screen);
        console.log(`✅ Video deletion from Cloudflare completed for screen ${screenId}`);
      } catch (error) {
        console.error(`❌ Failed to delete video from Cloudflare for screen ${screenId}:`, error);
        // Continue with screen deletion even if Cloudflare deletion fails
      }

      // Delete images from Cloudflare (main screen image and slideshow images)
      console.log('📸 Checking for image files to delete from Cloudflare...');
      try {
        // Delete main screen image if it exists
        if (screen.imageUrl) {
          console.log(`🖼️ Found image URL in screen: ${screen.imageUrl}`);
          const imageFileName = this.extractFileNameFromUrl(screen.imageUrl);
          if (imageFileName) {
            console.log(`🗑️ Will delete image files from Cloudflare: ${imageFileName}`);
            await fileService.deleteFile(imageFileName);
            console.log(`✅ Image deletion from Cloudflare completed for screen ${screenId}`);
          }
        }
        
        // Delete slideshow images and videos
        if (screen.slidesData && screen.slidesData.length > 0) {
          console.log(`🖼️ Checking ${screen.slidesData.length} slides for image and video URLs...`);
          const slideFilePromises = [];
          
          screen.slidesData.forEach((slide, index) => {
            // Delete image files if they exist
            if (slide.imageUrl) {
              console.log(`🖼️ Found image URL in slide ${index}: ${slide.imageUrl}`);
              const imageFileName = this.extractFileNameFromUrl(slide.imageUrl);
              if (imageFileName) {
                console.log(`🗑️ Will delete slide image files from Cloudflare: ${imageFileName}`);
                slideFilePromises.push(fileService.deleteFile(imageFileName));
              }
            }
            
            // Delete video files if they exist
            if (slide.videoUrl) {
              console.log(`🎥 Found video URL in slide ${index}: ${slide.videoUrl}`);
              const videoFileName = this.extractFileNameFromUrl(slide.videoUrl);
              if (videoFileName) {
                console.log(`🗑️ Will delete slide video files from Cloudflare: ${videoFileName}`);
                slideFilePromises.push(fileService.deleteFile(videoFileName));
              }
            }
          });
          
          if (slideFilePromises.length > 0) {
            await Promise.all(slideFilePromises);
            console.log(`✅ All slide file deletions from Cloudflare completed for screen ${screenId}`);
          }
        }
      } catch (error) {
        console.error(`❌ Failed to delete images from Cloudflare for screen ${screenId}:`, error);
        // Continue with screen deletion even if Cloudflare deletion fails
      }

      // Invalidate cache to ensure fresh data
      console.log('🔄 Clearing cache...');
      this.clearCache();
      console.log('✅ Cache cleared');

      console.log(`🎉 Screen ${screenId} deletion process completed successfully`);
      return true;
    } catch (error) {
      console.error('❌ Error deleting screen:', error);
      throw error;
    }
  }

  /**
   * Set default screen for a user
   * Based on DigiScreenManager logic
   */
  async setDefaultScreen(userId, screenKey, isDefaultScreen) {
    try {
      // Get all user screens
      const screens = await this.getUserScreens(userId, false); // Force fresh data
      const promises = [];

      screens.forEach(screen => {
        if (isDefaultScreen) {
          // Setting a screen as default
          if (screenKey === screen.key && !screen.isDefault) {
            // Set the target screen as default
            promises.push(this.updateDocument(screen.key, { isDefault: true }));
          }
          if (screenKey !== screen.key && screen.isDefault) {
            // Remove default from other screens
            promises.push(this.updateDocument(screen.key, { isDefault: false }));
          }
        } else {
          // Removing default from a screen
          if (screenKey === screen.key && screen.isDefault) {
            // Remove default from target screen
            promises.push(this.updateDocument(screen.key, { isDefault: false }));
          }
        }
      });

      // Execute all updates
      await Promise.all(promises);
      
      // Invalidate cache to ensure fresh data
      this.clearCache();
      
      return true;
    } catch (error) {
      console.error('Error setting default screen:', error);
      throw error;
    }
  }

  /**
   * Modify an existing screen (migrated from DigiScreenManager)
   */
  async modifyScreen(screenKey, screenData, imageVideoObject, userId) {
    let stateUrl = '';
    let curScr = null;
    let scrVer = 0;
    if (!screenData.name || screenData.name === '') {
      throw { message: 'Please provide the screen name' };
    }
    // Progress reporting (optional)
    // firebaseService.onProgress('Validating screen data');
    // Get all screens for the user
    const screens = await this.getUserScreens(userId, false);
    curScr = screens.find(screen => screen.key === screenKey);
    scrVer = (curScr && curScr.version) ? curScr.version : 0;
    if (screens.find(screen => screen.name === screenData.name && screen.key !== screenKey)) {
      throw { message: `A screen with the '${screenData.name}' name already exists` };
    }
    // firebaseService.onProgress('Updating screen data');
    screenData.modified = new Date().getTime();
    if (imageVideoObject && imageVideoObject.indexOf('http') === 0) {
      await Promise.all([
        this.updateDocument(screenKey, screenData),
        this.setDefaultScreen(userId, screenKey, screenData.isDefault)
      ]);
    } else {
      // Delete old files
      await fileService.deleteFiles(screenKey);
      // Determine file name
      let fileName = screenKey + '_image.jpeg';
      if (imageVideoObject && imageVideoObject.indexOf('data:image/png;base64') !== -1) {
        fileName = screenKey + '_image.png';
      } else if (imageVideoObject && imageVideoObject.indexOf('data:image/jpg;base64') !== -1) {
        fileName = screenKey + '_image.jpg';
      } else if (imageVideoObject && imageVideoObject.indexOf('data:video/mp4;base64') !== -1) {
        fileName = screenKey + '_video.mp4';
      }
      // firebaseService.onProgress('Uploading screen file');
      const uploadResult = await fileService.uploadFile(fileName, imageVideoObject);
      if (fileName.indexOf('.mp4') !== -1) {
        // For video files - uploadResult contains Cloudflare URL and thumbnail URLs
        screenData.videoUrl = uploadResult.videoUrl;
        screenData.thumbUrl = uploadResult.thumbUrl;
        if (scrVer > 1) {
          screenData.thumbUniversalUrl = uploadResult.thumbUniversalUrl;
        }
      } else {
        // For image files - uploadResult can be a string (legacy) or an object (new client-side resizing)
        if (typeof uploadResult === 'object' && uploadResult.imageUrl && uploadResult.imageUniversalUrl) {
          // New format - object with both URLs
          screenData.imageUrl = uploadResult.imageUrl;
          screenData.imageUniversalUrl = uploadResult.imageUniversalUrl;
        } else {
          // Legacy format - single URL string
          screenData.imageUrl = uploadResult;
          if (scrVer > 1) {
            screenData.imageUniversalUrl = uploadResult.replace('_1920x1080.', '_1920x1920.');
          }
        }
        screenData.stateUrl = stateUrl;
      }
      await Promise.all([
        this.updateDocument(screenKey, screenData),
        this.setDefaultScreen(userId, screenKey, screenData.isDefault)
      ]);
    }
    // Invalidate cache to ensure fresh data
    this.clearCache();
    return { key: screenKey, ...screenData };
  }

  /**
   * Get thumbnail URL for an image/video URL
   * Converts high-resolution URLs to thumbnail URLs
   * @param {string} url - The original URL
   * @param {boolean} noCache - Whether to add cache-busting parameter
   * @returns {string} Thumbnail URL
   */
  static getThumbnail(url, noCache = false) {
    if (!url) return '';
    
    // Handle video placeholders
    if (url.indexOf("video") !== -1 && url.indexOf("video_thumb") === -1) {
      return "/client/screens/images/common/video_placeholder.png";
    }
    
    // For Cloudflare URLs, return as-is since they handle thumbnails differently
    if (url.includes('r2.dev') || url.includes('cloudflare')) {
      let resUrl = url;
      if (noCache) {
        resUrl += (resUrl.includes('?') ? '&' : '?') + '_t=' + Date.now();
      }
      return resUrl;
    }
    
    // Convert high-res URLs to thumbnail URLs
    let resUrl = url
      .replace("_1920x1080.", "_640x360.")
      .replace("_1920x1920.", "_640x640.")
      .replace("_1080x1920.", "_360x640.")
      .replace("_video", "_video_thumb");
    
    // Handle video file extensions
    if (url.includes('.mp4')) {
      resUrl = url.replace(/\.mp4$/, '_640x360.jpeg');
    }
    
    // Add cache-busting parameter if noCache is true
    if (resUrl && noCache) {
      resUrl += (resUrl.includes('?') ? '&' : '?') + '_t=' + Date.now();
    }
    
    return resUrl;
  }

  /**
   * Get full-size image URL from thumbnail URL
   * @param {string} url - The thumbnail URL
   * @returns {string} Full-size URL
   */
  static getFullSizeImageUrl(url) {
    return url.replace("_640x360.", "_1920x1080.").replace("_640x640.", "_1920x1920.");
  }

  /**
   * Create a slideshow screen with multiple images/videos
   * Migrated from DigiScreenManager.addSlides
   * @param {Object} screenData - Screen data object
   * @param {Array} rawImages - Array of base64 image/video data
   * @param {string} userId - User ID
   * @param {Function} onProgress - Optional progress callback
   * @returns {Promise<Object>} Created screen data
   */
  async addSlides(screenData, rawImages, userId, onProgress) {
    try {
      // Validate input
      if (!screenData.name || screenData.name.trim() === '') {
        throw { message: 'Please provide the screen name' };
      }
      if (!rawImages || rawImages.length === 0) {
        throw { message: 'Please provide at least one image or video' };
      }
      if (!userId) {
        throw { message: 'User ID is required' };
      }

      // Check for duplicate screen name
      const screens = await this.getUserScreens(userId, false);
      if (screens.find(screen => screen.name === screenData.name)) {
        throw { message: `A screen with the '${screenData.name}' name already exists` };
      }

      // Report progress
      if (onProgress) onProgress('Creating screen');

      // Prepare screen data
      screenData.uid = userId;
      screenData.created = new Date().getTime();
      screenData.version = 2;

      // Create the screen document
      const result = await this.createDocument(screenData);
      const screenKey = result.key;

      // Set as default if specified
      if (screenData.isDefault) {
        if (onProgress) onProgress('Setting default screen');
        await this.setDefaultScreen(userId, screenKey, true);
      }

      // Upload all slide files
      if (onProgress) onProgress('Uploading screen files');
      
      const uploadPromises = rawImages.map(async (image, index) => {
        // Determine file type and name
        let fileName = `${screenKey}_slide_${new Date().getTime()}_${index}.jpeg`;
        if (image.indexOf("data:image/png;base64") !== -1) {
          fileName = `${screenKey}_slide_${new Date().getTime()}_${index}.png`;
        } else if (image.indexOf("data:image/jpg;base64") !== -1) {
          fileName = `${screenKey}_slide_${new Date().getTime()}_${index}.jpg`;
        } else if (image.indexOf("data:video/mp4;base64") !== -1) {
          fileName = `${screenKey}_slide_video_${new Date().getTime()}_${index}.mp4`;
        }

        // Upload file using FileService
        const uploadResult = await fileService.uploadFile(fileName, image);
        
        // Report progress for each file
        if (onProgress) {
          onProgress(`Uploading file ${index + 1} of ${rawImages.length}`);
        }

        return { uploadResult, index, isVideo: fileName.indexOf("_slide_video_") !== -1 };
      });

      // Wait for all uploads to complete
      const uploadResults = await Promise.all(uploadPromises);

      // Update screen data with slide URLs
      const updatedSlidesData = screenData.slidesData.map((slide, index) => {
        const uploadResult = uploadResults.find(result => result.index === index);
        if (!uploadResult) return slide;

        if (uploadResult.isVideo) {
          // Handle video slides
          return {
            ...slide,
            videoUrl: uploadResult.uploadResult.videoUrl,
            thumbUrl: uploadResult.uploadResult.thumbUrl,
            thumbUniversalUrl: uploadResult.uploadResult.thumbUniversalUrl
          };
        } else {
          // Handle image slides
          // uploadResult.uploadResult can be either a string (legacy) or an object (new client-side resizing)
          let imageUrl, imageUniversalUrl;
          
          if (typeof uploadResult.uploadResult === 'string') {
            // Legacy format - single URL string
            imageUrl = uploadResult.uploadResult;
            imageUniversalUrl = uploadResult.uploadResult.replace("_1920x1080.", "_1920x1920.");
          } else if (typeof uploadResult.uploadResult === 'object' && uploadResult.uploadResult.imageUrl) {
            // New format - object with both URLs
            imageUrl = uploadResult.uploadResult.imageUrl;
            imageUniversalUrl = uploadResult.uploadResult.imageUniversalUrl;
          } else {
            console.error('Invalid upload result for image slide:', uploadResult);
            return slide;
          }
          
          return {
            ...slide,
            imageUrl: imageUrl,
            imageUniversalUrl: imageUniversalUrl
          };
        }
      });

      // Update the screen with slide data
      await this.updateDocument(screenKey, {
        slidesData: updatedSlidesData,
        version: 2
      });

      // Invalidate cache to ensure fresh data
      this.clearCache();

      return { key: screenKey, ...screenData, slidesData: updatedSlidesData };
    } catch (error) {
      console.error('Error creating slideshow screen:', error);
      throw error;
    }
  }

  /**
   * Update an existing slideshow screen
   * Migrated from DigiScreenManager.modifySlides
   */
  async updateSlideshow(screenKey, screenData, rawImages, userId) {
    try {
      // Get original screen data to compare
      const originalScreen = await this.getScreen(screenKey, false);
      const deletedSlides = [];
      
      // Find deleted slides by comparing old and new slidesData
      if (originalScreen && originalScreen.slidesData && originalScreen.slidesData.length > 0) {
        // Create a set of URLs from the new slidesData for faster lookup
        const newSlideUrls = new Set();
        if (screenData.slidesData && screenData.slidesData.length > 0) {
          screenData.slidesData.forEach(newSlide => {
            if (newSlide.imageUrl) newSlideUrls.add(newSlide.imageUrl);
            if (newSlide.imageUniversalUrl) newSlideUrls.add(newSlide.imageUniversalUrl);
            if (newSlide.videoUrl) newSlideUrls.add(newSlide.videoUrl);
          });
        }
        
        // Check each old slide - if its URL is not in the new set, it was deleted
        originalScreen.slidesData.forEach((oldSlide, index) => {
          let isDeleted = false;
          
          // Check if this slide's URLs exist in the new slides
          if (oldSlide.imageUrl && !newSlideUrls.has(oldSlide.imageUrl)) {
            isDeleted = true;
          } else if (oldSlide.videoUrl && !newSlideUrls.has(oldSlide.videoUrl)) {
            isDeleted = true;
          }
          
          if (isDeleted) {
            console.log(`🗑️ Slide ${index} was deleted, will clean up files`);
            deletedSlides.push(oldSlide);
          }
        });
      }
      
      // Delete files for removed slides
      if (deletedSlides.length > 0) {
        console.log(`🗑️ Found ${deletedSlides.length} deleted slides, cleaning up files...`);
        for (const deletedSlide of deletedSlides) {
          if (deletedSlide.imageUrl) {
            const imageFileName = this.extractFileNameFromUrl(deletedSlide.imageUrl);
            if (imageFileName) {
              console.log(`🗑️ Deleting deleted slide image from Cloudflare: ${imageFileName}`);
              await fileService.deleteFile(imageFileName);
            }
          }
          if (deletedSlide.videoUrl) {
            const videoFileName = this.extractFileNameFromUrl(deletedSlide.videoUrl);
            if (videoFileName) {
              console.log(`🗑️ Deleting deleted slide video from Cloudflare: ${videoFileName}`);
              await fileService.deleteFile(videoFileName);
            }
          }
        }
      }
      
      // Upload new slide files if provided
      if (rawImages && rawImages.length > 0) {
        // Find the positions of new slides first
        const newSlidePositions = [];
        screenData.slidesData.forEach((slide, index) => {
          // If slide doesn't have imageUrl, imageUniversalUrl, or videoUrl, it's a new slide
          if (!slide.imageUrl && !slide.imageUniversalUrl && !slide.videoUrl) {
            newSlidePositions.push(index);
          }
        });

        const uploadPromises = rawImages.map(async (image, index) => {
          const slidePosition = newSlidePositions[index];
          
          // Determine file type and name using the actual slide position
          let fileName = `${screenKey}_slide_${new Date().getTime()}_${slidePosition}.jpeg`;
          if (image.indexOf("data:image/png;base64") !== -1) {
            fileName = `${screenKey}_slide_${new Date().getTime()}_${slidePosition}.png`;
          } else if (image.indexOf("image/jpg;base64") !== -1) {
            fileName = `${screenKey}_slide_${new Date().getTime()}_${slidePosition}.jpg`;
          } else if (image.indexOf("data:video/mp4;base64") !== -1) {
            fileName = `${screenKey}_slide_video_${new Date().getTime()}_${slidePosition}.mp4`;
          }

          // Upload file using FileService
          const uploadResult = await fileService.uploadFile(fileName, image);
          
          return { uploadResult, index, slidePosition, isVideo: fileName.indexOf("_slide_video_") !== -1 };
        });

        // Wait for all uploads to complete
        const uploadResults = await Promise.all(uploadPromises);

        // Update slidesData with new file URLs using the slidePosition from upload results
        uploadResults.forEach((result) => {
          const slideIndex = result.slidePosition;
          if (slideIndex !== undefined && slideIndex < screenData.slidesData.length) {
            if (result.isVideo) {
              // Handle video slides
              screenData.slidesData[slideIndex] = {
                ...screenData.slidesData[slideIndex],
                videoUrl: result.uploadResult.videoUrl,
                thumbUrl: result.uploadResult.thumbUrl,
                thumbUniversalUrl: result.uploadResult.thumbUniversalUrl
              };
            } else {
              // Handle image slides
              // result.uploadResult can be either a string (legacy) or an object (new client-side resizing)
              let imageUrl, imageUniversalUrl;
              
              if (typeof result.uploadResult === 'string') {
                // Legacy format - single URL string
                imageUrl = result.uploadResult;
                imageUniversalUrl = result.uploadResult.replace("_1920x1080.", "_1920x1920.");
              } else if (typeof result.uploadResult === 'object' && result.uploadResult.imageUrl) {
                // New format - object with both URLs
                imageUrl = result.uploadResult.imageUrl;
                imageUniversalUrl = result.uploadResult.imageUniversalUrl;
              } else {
                console.error('Invalid upload result for image slide:', result);
                return;
              }
              
              screenData.slidesData[slideIndex] = {
                ...screenData.slidesData[slideIndex],
                imageUrl: imageUrl,
                imageUniversalUrl: imageUniversalUrl
              };
            }
          }
        });
      }

      // Update screen data
      const updatePromises = [
        this.updateDocument(screenKey, screenData),
        this.setDefaultScreen(userId, screenKey, screenData.isDefault)
      ];

      await Promise.all(updatePromises);

      // Clean up unused files
      await this.cleanNotUsedSlideScreenFiles(screenKey);

      // Invalidate cache to ensure fresh data
      this.clearCache();

      return { key: screenKey, ...screenData };
    } catch (error) {
      console.error('Error updating slideshow screen:', error);
      throw error;
    }
  }

  /**
   * Delete video from Cloudflare based on screen data
   * This handles the case where videos are stored in Cloudflare but not in Firebase
   */
  async deleteVideoFromCloudflare(screenId) {
    try {
      console.log(`🔍 Checking screen ${screenId} for video URLs to delete from Cloudflare...`);
      
      // Get the screen data to find video URLs
      const screen = await this.getScreen(screenId, false); // Force fresh data
      if (!screen) {
        console.log(`⚠️ Screen ${screenId} not found, skipping Cloudflare deletion`);
        return;
      }

      await this.deleteVideoFromCloudflareUsingScreenData(screen);
    } catch (error) {
      console.error(`❌ Error deleting video from Cloudflare for screen ${screenId}:`, error);
      throw error;
    }
  }

  /**
   * Delete video from Cloudflare using provided screen data
   * This method takes screen data directly instead of fetching it
   */
  async deleteVideoFromCloudflareUsingScreenData(screen) {
    try {
      console.log(`🔍 Checking screen data for video URLs to delete from Cloudflare...`);
      
      const deletionPromises = [];

      // Check for main video URL
      if (screen.videoUrl) {
        console.log(`🎥 Found video URL in screen: ${screen.videoUrl}`);
        const fileName = this.extractFileNameFromUrl(screen.videoUrl);
        if (fileName) {
          console.log(`🗑️ Will delete video file from Cloudflare: ${fileName}`);
          deletionPromises.push(this.deleteFileFromCloudflare(fileName));
        }
      }

      // Check for slideshow videos
      if (screen.slidesData && screen.slidesData.length > 0) {
        console.log(`🎬 Checking ${screen.slidesData.length} slides for video URLs...`);
        screen.slidesData.forEach((slide, index) => {
          if (slide.videoUrl) {
            console.log(`🎥 Found video URL in slide ${index}: ${slide.videoUrl}`);
            const fileName = this.extractFileNameFromUrl(slide.videoUrl);
            if (fileName) {
              console.log(`🗑️ Will delete slide video file from Cloudflare: ${fileName}`);
              deletionPromises.push(this.deleteFileFromCloudflare(fileName));
            }
          }
        });
      }

      // Execute all deletions
      if (deletionPromises.length > 0) {
        console.log(`⚡ Executing ${deletionPromises.length} Cloudflare deletions...`);
        await Promise.all(deletionPromises);
        console.log(`✅ All Cloudflare deletions completed for screen ${screen.key || 'unknown'}`);
      } else {
        console.log(`ℹ️ No video files found to delete from Cloudflare for screen ${screen.key || 'unknown'}`);
      }

    } catch (error) {
      console.error(`❌ Error deleting video from Cloudflare for screen ${screen.key || 'unknown'}:`, error);
      throw error;
    }
  }

  /**
   * Clean up unused slide files for a screen
   * Migrated from DigiScreenManager.cleanNotUsedSlideScreenFiles
   */
  async cleanNotUsedSlideScreenFiles(screenKey) {
    try {
      // Get current screen data
      const screen = await this.getScreen(screenKey, false);
      if (!screen || !screen.slidesData) {
        return;
      }

      // Collect all currently used file URLs
      const slidesUrls = [];
      const fileProperties = ["imageUrl", "imageUniversalUrl", "videoUrl", "thumbUrl", "thumbUniversalUrl"];
      
      screen.slidesData.forEach(slide => {
        fileProperties.forEach(property => {
          if (slide[property]) {
            slidesUrls.push(slide[property]);
          }
        });
      });

      // Get all files for this screen from Firebase
      const firebaseFiles = await firebaseService.getFiles(`${screenKey}_slide_`);
      
      // Find and delete unused Firebase files
      const deletePromises = [];
      
      firebaseFiles.forEach(file => {
        let isInUse = false;
        const fileName = file.split('/')[1];
        
        slidesUrls.forEach(slideUrl => {
          if (slideUrl.indexOf(fileName.split("_192")[0]) !== -1 || 
              slideUrl.indexOf(fileName.split("_640")[0]) !== -1) {
            isInUse = true;
          }
        });
        
        if (!isInUse) {
          deletePromises.push(fileService.deleteFile(file));
        }
      });

      // Also check for Cloudflare files that might need cleanup
      // This is more complex since we need to check video URLs specifically
      const cloudflareDeletionPromises = [];
      
      slidesUrls.forEach(slideUrl => {
        // Only process Cloudflare URLs
        if (slideUrl && slideUrl.includes('r2.dev')) {
          const fileName = this.extractFileNameFromUrl(slideUrl);
          if (fileName && fileName.includes('_slide_')) {
            // Check if this file is still referenced in the current slides
            let isStillReferenced = false;
            screen.slidesData.forEach(slide => {
              const fileProperties = ["imageUrl", "imageUniversalUrl", "videoUrl", "thumbUrl", "thumbUniversalUrl"];
              fileProperties.forEach(property => {
                if (slide[property] && slide[property].includes(fileName)) {
                  isStillReferenced = true;
                }
              });
            });
            
            if (!isStillReferenced) {
              console.log(`🗑️ Found unused Cloudflare file: ${fileName}`);
              cloudflareDeletionPromises.push(this.deleteFileFromCloudflare(fileName));
            }
          }
        }
      });

      // Delete all unused files
      if (deletePromises.length > 0 || cloudflareDeletionPromises.length > 0) {
        await Promise.all([...deletePromises, ...cloudflareDeletionPromises]);
        console.log(`Cleaned up ${deletePromises.length} unused Firebase files and ${cloudflareDeletionPromises.length} unused Cloudflare files for screen ${screenKey}`);
      }
    } catch (error) {
      console.error('Error cleaning unused slide files:', error);
      // Don't throw error as this is not critical for the main operation
    }
  }

  /**
   * Extract filename from URL (handles both Firebase and Cloudflare URLs)
   */
  extractFileNameFromUrl(url) {
    if (!url) return null;
    
    try {
      // Handle Cloudflare URLs
      if (url.includes('r2.dev')) {
        const parts = url.split('/');
        return parts[parts.length - 1]; // Get the last part as filename
      }
      
      // Handle Firebase URLs
      if (url.includes('firebasestorage.googleapis.com')) {
        const parts = url.split('/');
        const lastPart = parts[parts.length - 1];
        // Remove query parameters
        return lastPart.split('?')[0];
      }
      
      console.warn(`⚠️ Unknown URL format for filename extraction: ${url}`);
      return null;
    } catch (error) {
      console.error(`❌ Error extracting filename from URL ${url}:`, error);
      return null;
    }
  }

  /**
   * Delete a single file from Cloudflare
   */
  async deleteFileFromCloudflare(fileName) {
    try {
      console.log(`☁️ Deleting file from Cloudflare: ${fileName}`);
      const { default: cloudflareService } = await import('./CloudflareService.js');
      await cloudflareService.deleteFile(fileName);
      console.log(`✅ Successfully deleted from Cloudflare: ${fileName}`);
    } catch (error) {
      console.error(`❌ Failed to delete ${fileName} from Cloudflare:`, error);
      // Don't throw error to avoid failing the entire deletion process
    }
  }
}

// Create singleton instance
const screenService = new ScreenService();
export { ScreenService };
export default screenService;